import React, { useEffect, useState } from 'react';
import { Box, Button, Slider, Typography } from '@mui/material';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from './ScrollableDialog';
import PrimaryActionButton from './PrimaryActionButton';
import { cropImageToFile } from '../../utils/imageCrop';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const DEFAULT_CROP = { x: 0, y: 0 };
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

const ImageCropDialog = ({
  open,
  imageSrc,
  fileName,
  onClose,
  onApply,
  aspect = 1,
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState(DEFAULT_CROP);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCrop(DEFAULT_CROP);
    setZoom(MIN_ZOOM);
    setCroppedAreaPixels(null);
    setApplying(false);
    setErrorKey(null);
  }, [open, imageSrc]);

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels || applying) {
      return;
    }

    setApplying(true);
    setErrorKey(null);
    try {
      const file = await cropImageToFile(imageSrc, croppedAreaPixels, fileName);
      await onApply(file);
    } catch (error) {
      console.error('Failed to crop image:', error);
      setErrorKey('stores.images.error.cropFailed');
      setApplying(false);
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={applying ? undefined : onClose}
      maxWidth="sm"
      title={t('stores.images.cropTitle')}
      showCloseButton={!applying}
      dividers
      titleSx={{ pb: 1, flexShrink: 0 }}
      contentSx={{ overflow: 'hidden', maxHeight: 'none' }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={applying} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleApply} disabled={applying || !croppedAreaPixels}>
            {applying ? t('common.saving') : t('common.apply')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box
        data-testid="image-crop-viewport"
        style={{ backgroundColor: BRAND.surface }}
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 280, sm: 360 },
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {imageSrc ? (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape="rect"
            showGrid
            roundCropAreaPixels
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            restrictPosition={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
          />
        ) : null}
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
        {t('stores.images.cropZoom')}
      </Typography>
      <Slider
        value={zoom}
        min={MIN_ZOOM}
        max={MAX_ZOOM}
        step={0.1}
        onChange={(_, value) => setZoom(Array.isArray(value) ? value[0] : value)}
        disabled={applying}
        aria-label={t('stores.images.cropZoom')}
        sx={{
          color: BRAND.accent,
          '& .MuiSlider-thumb': { '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 8px ${brandAlpha('accent', 0.16)}` } },
        }}
      />
      {errorKey ? (
        <Typography variant="caption" sx={{ color: '#f44336', display: 'block' }}>
          {t(errorKey)}
        </Typography>
      ) : null}
    </ScrollableDialog>
  );
};

export default ImageCropDialog;
